import { assign, setup } from "xstate";

import { knockoutMachine } from "@/features/tournament/knockout/knockout.machine";
import { generateKnockoutGraph } from "@/features/tournament/knockout/knockoutGraph";
import { roundRobinMachine } from "@/features/tournament/roundrobin/roundrobin.machine";
import { generateRoundRobinGraph } from "@/features/tournament/roundrobin/roundRobinGraph";
import { swissMachine } from "@/features/tournament/swiss/swiss.machine";
import {
  sendTournamentResult,
  type TournamentActorRef,
} from "@/features/tournament/tournamentAdapters";
import {
  type Activity,
  isFreeForAll,
  isTeams,
  isTournament,
  type Player,
  type TournamentActivity,
  TournamentFormat,
} from "@/types";
import type { PlayerId } from "@/types/branded";
import { shuffle } from "@/utils/index";

// ── Team generation ────────────────────────────────────────────

export type Teams = PlayerId[][];

export const generateRandomTeams = (
  numTeams: number,
  teamSizes: number[],
  participantIds: PlayerId[],
): Teams => {
  const newTeams: Teams = Array.from({ length: numTeams }, () => []);
  const shuffled = shuffle(participantIds);

  let i = 0;
  const take = () => shuffled[i++];

  teamSizes.forEach((size = 0, t) => {
    for (let j = 0; j < size && i < shuffled.length; j++) {
      newTeams[t]!.push(take()!);
    }
  });

  const targets = teamSizes.some((s) => (s ?? 0) === 0)
    ? teamSizes.map((s, i) => ((s ?? 0) === 0 ? i : -1)).filter((i) => i !== -1)
    : Array.from({ length: numTeams }, (_, i) => i);

  let t = 0;
  while (i < shuffled.length) {
    newTeams[targets[t % targets.length]!]!.push(take()!);
    t++;
  }

  return newTeams;
};

export const createTeamsForActivity = (
  selectedActivity: Activity,
  playerIds: PlayerId[],
): PlayerId[][] => {
  const { participantIds } = selectedActivity;
  const players = participantIds.length > 0 ? participantIds : playerIds;

  if (isFreeForAll(selectedActivity)) {
    return players.map((pid) => [pid]);
  }

  if (isTournament(selectedActivity)) {
    return [];
  }

  if (isTeams(selectedActivity)) {
    const { numTeams, randomizeTeams, teamSizes } = selectedActivity;
    return randomizeTeams
      ? generateRandomTeams(numTeams, teamSizes, players)
      : Array.from({ length: numTeams }, () => []);
  }

  throw new Error("Unsupported activity format");
};

// ── Types (formerly matchFlow.types.ts) ────────────────────────

export type RankEntry = readonly [teamIndex: number, rank: number];

export interface MatchFlowContext {
  activity: Activity | null;
  participantIds: PlayerId[];
  teams: Teams;
  rankEntries: RankEntry[];
  tournamentRef?: TournamentActorRef;
  tournamentFormat?: TournamentFormat;
}

export type SelectActivityEvent = {
  type: "activity.select";
  activity: Activity;
  participantIds: PlayerId[];
  teams: Teams;
  players: Record<PlayerId, Player>;
};

export type SetTeamsEvent = { type: "teams.set"; teams: Teams };
export type ConfirmTeamsEvent = { type: "teams.confirm" };
export type AssignRankEvent = { type: "rank.assign"; teamIndex: number; tie: boolean };
export type UndoRankEvent = { type: "rank.undo"; teamIndex: number };
export type SubmitResultsEvent = { type: "results.submit" };
export type ConfirmResultsEvent = { type: "results.confirm" };
export type RecordTournamentResultEvent = {
  type: "tournament.recordResult";
  matchId: string;
  winnerId: PlayerId | "draw";
};
export type AdvanceSwissRoundEvent = { type: "tournament.advanceSwiss" };
export type FinishTournamentEvent = { type: "tournament.finish" };
export type BackEvent = { type: "flow.back" };
export type ResetEvent = { type: "flow.reset" };

export type MatchFlowEvent =
  | SelectActivityEvent
  | SetTeamsEvent
  | ConfirmTeamsEvent
  | AssignRankEvent
  | UndoRankEvent
  | SubmitResultsEvent
  | ConfirmResultsEvent
  | RecordTournamentResultEvent
  | AdvanceSwissRoundEvent
  | FinishTournamentEvent
  | BackEvent
  | ResetEvent;

export type MatchFlowPhase =
  | "idle"
  | "routing"
  | "setupTeams"
  | "recording"
  | "tournament.setup"
  | "tournament.inProgress"
  | "tournament.completed"
  | "reviewing";

export const deriveNextRank = (entries: RankEntry[]): number => {
  if (entries.length === 0) return 1;

  let max = 0;
  let countAtMax = 0;

  for (const [, r] of entries) {
    if (r > max) {
      max = r;
      countAtMax = 1;
    } else if (r === max) {
      countAtMax++;
    }
  }

  return max + countAtMax;
};

export const groupByRank = (
  entries: readonly RankEntry[],
): Array<readonly [rank: number, teamIndices: number[]]> => {
  const map = new Map<number, number[]>();

  for (const [teamIndex, rank] of entries) {
    const list = map.get(rank);
    if (list) {
      list.push(teamIndex);
    } else {
      map.set(rank, [teamIndex]);
    }
  }

  return Array.from(map.entries()).sort(([a], [b]) => a - b);
};

const DEFAULT_KNOCKOUT_ELIMINATIONS = 1;

const getKEliminationCount = (activity: TournamentActivity): number => {
  if (activity.knockoutEliminations != null) {
    return Math.max(1, Math.floor(activity.knockoutEliminations));
  }

  if (activity.tournamentFormat === TournamentFormat.DoubleElimination) return 2;

  return DEFAULT_KNOCKOUT_ELIMINATIONS;
};

const isKEliminationFormat = (format: TournamentFormat): boolean =>
  format === TournamentFormat.SingleElimination || format === TournamentFormat.DoubleElimination;

export const matchFlowMachine = setup({
  types: {
    context: {} as MatchFlowContext,
    events: {} as MatchFlowEvent,
  },

  guards: {
    hasMinimumTeams: ({ context }) => context.teams.filter((t) => t.length > 0).length >= 2,

    allTeamsRanked: ({ context }) => {
      const nonEmpty = context.teams.filter((t) => t.length > 0);
      return context.rankEntries.length === nonEmpty.length;
    },

    isTeamsActivity: ({ context }) => isTeams(context.activity ?? undefined),

    isTournamentActivity: ({ context }) => isTournament(context.activity ?? undefined),

    isFfaActivity: ({ context }) => isFreeForAll(context.activity ?? undefined),
  },

  actions: {
    resetContext: assign({
      activity: null,
      participantIds: [],
      teams: [],
      rankEntries: [],
      tournamentRef: undefined,
      tournamentFormat: undefined,
    }),

    recordTournamentResult: ({ context, event }) => {
      if (event.type !== "tournament.recordResult") return;
      if (!context.tournamentRef) return;
      sendTournamentResult(context.tournamentRef, event.matchId, event.winnerId);
    },
  },
  actors: {
    knockoutTournament: knockoutMachine,
    roundRobinTournament: roundRobinMachine,
    swissTournament: swissMachine,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCGAXAxgCwGIBsB7AdwGIAzI4gOgCc4x0BtABgF1FQAHQ2AS3R9CAO04gAHogC0ARgCsANjnUAnAoDsADhUBmOSrkyATCYA0IAJ6ItK6iwUGWOmQs0KH6gL6fzaLHipqPgh8MFJUTEEANwELalgwUMjWDiQQHn5BETFJBFkTBWodHScVFyMZdUVzKwQbOwc5Jxc3BwVvXwwcAhI6QgBXQWEoUhSxDIEhUTTcqU0jFlV1I00mlpVNFnUaxE0ZHWp1JTlNABYWU5VTs-afED9uwNoBoZGmGVTuXknsmelVg4sLYuU4LXTFU47BAyFgyGTUfQGORVTQ6KpyDr3LoBXrPQZ8YajIyfdLfLLTUCzfSnagKPSGU4yebqdT7KEwuEIlRIlFouSGTEPHE0BLofpcAAqYFQyFgpHQ0tl8UYYzSE3JOWkp1B1CMOj2SiMCmMmj2UM0VWooKuRnUpwUjMMOkF2J6IsY4qlMrlCu91EwInIfFoyFVX0yU01eRcB1trJ0DucMg2unNnMM-NRKlhKx0Rhd-jdyrFksVcsovQARhEANZh0kR36U6RlGnXJQ6e38-k6FRQtZW2GubWXdwaZ13IVF0WessUJ4MZjscZkyN-PInNuo-l0hZbC1QhPwlgGY0VO2KU4YyeuhcB2gQAkjWioYQ16ioWD8KDCevqtfNgg2rwvsLL6LC+pXn2liINm6hWkYNpnGU8yXAWjy4mA96PoSL5vh+X58D+7wkv+TYSIgoLwfuuhOB4iEKOysI0iodrGOccjAcs6HCnQWGEA+T6kHh779MIECEH+q7kbk1y2CU4ILDC3IaJCMEIO4NIXFcJTFBaqK3J0hZ3gJOHPnA-T4OgsDxP0lbIAIUmNhSFEafI1AuEC6jcnmxpyEYabwhmJy9jmqI8UW9DYUJFY0NWmB1suarSS5uRMos8iduozj+YocKMep9rwnSCZUQY8hGhFJmCYSsXUPFdYfCuzlRsmhQlCw-nzLlzJQkayj2p1ym2juKhVb06ADLQwgymAwjoMW4qjEl4Y-Kl0hNLYLJVKcaJDvYSjsvSCL7MU+pGoh4U3sZE1TTNyBzQtBIAArPFA9BfvKd2zfNfHYQAstiTlrVGUiXJoupuFcQJova3Lstqthw1oCiwgY2XjTQk39NNP1PcIr2EO9cA+t9D2-agEBRK+mBgAAysQfBfsDGrrlIyLwSjSLag61xyOy55FLtqPZsaOleNdGFY2Tj1BATb0faTOP3bLQbCEz2AswBrnKMmXVqNyyZ6tqCOXFaHhuGjyK2pj1DY7j5P44TxOfXVDVazJ0h6gceZaIhQILHSyLsgoiEIoYJ4bEYhjorb9sq79L0KyTy2kSloMQ3CegrMsiFqKy0G1OxRh2L2rjclRJzXkZUt2zLv0BsgXChAqECp81IPrvlIF6aByz6qiR38idZ3naHGzXncwiEBAcBiFOVAd6zgGyMaBz59oejlSYAXqZUENnfYJjqBcaGS7xwShEv2uzPsVxFBd0eKCfBi77Uezwr23kJiwbjZfYts8SvGvp7PIGxFiwm0KiZY2Vdr83Uh-IorFdCoz-iUQyWIbruhLF6WUID1obk2FabUnYTzuH2CcQKSDv6oPHJ1QB-EapQHwVGaOLFkRfx7KxVYmgqFfxQb-Oh1dMG13jnjFhbM2KqH3CyOkDoyjbD3vsRYFRjSo3ONcPQGCF63WVnjRaXAJErwcOvGRRxSoKKOiUBEewDAT3KnHeuTtk5fiMa5KQJgDh-wuJ2dw3kwJDy8ejBYJQLy-0cXox2-pCBNxbpANxuQ8yqHkRceQqwYSh0CdQbQihVhlEQleW2sA7IOXQK3BJiA4RGBpDIYW8gYTeTSWmDKpwaFAl2moCc3ggA */
  id: "matchFlow",
  context: (): MatchFlowContext => ({
    activity: null,
    participantIds: [],
    teams: [],
    rankEntries: [],
    tournamentRef: undefined,
    tournamentFormat: undefined,
  }),
  initial: "idle",

  states: {
    idle: {
      on: {
        "activity.select": {
          target: "routing",
          actions: assign({
            activity: ({ event }) => {
              if (event.type !== "activity.select") return null;
              return event.activity;
            },
            participantIds: ({ event }) =>
              event.type === "activity.select" ? event.participantIds : [],
            teams: ({ event }) => (event.type === "activity.select" ? event.teams : []),
            rankEntries: () => [],
            tournamentRef: () => undefined,
            tournamentFormat: ({ event }) =>
              event.type === "activity.select" && isTournament(event.activity)
                ? event.activity.tournamentFormat
                : undefined,
          }),
        },
      },
    },

    routing: {
      always: [
        { target: "setupTeams", guard: "isTeamsActivity" },
        { target: "tournament", guard: "isTournamentActivity" },
        { target: "recording" },
      ],
    },
    tournament: {
      entry: assign(({ context, spawn }) => {
        if (context.tournamentRef) return {};

        const { activity, participantIds: players } = context;

        if (!activity || !isTournament(activity)) return {};

        if (isKEliminationFormat(activity.tournamentFormat)) {
          const initialGraph = generateKnockoutGraph(players, getKEliminationCount(activity));
          const ref = spawn("knockoutTournament", { input: { players, initialGraph } });
          return { tournamentRef: ref };
        }

        switch (activity.tournamentFormat) {
          case TournamentFormat.RoundRobin: {
            const initialGraph = generateRoundRobinGraph(players);
            const ref = spawn("roundRobinTournament", { input: { players, initialGraph } });
            return { tournamentRef: ref };
          }
          case TournamentFormat.Swiss: {
            const ref = spawn("swissTournament", { input: { players } });
            return { tournamentRef: ref };
          }
          default: {
            return {};
          }
        }
      }),
      on: {
        "tournament.recordResult": {
          actions: "recordTournamentResult",
        },

        "results.submit": {
          target: "reviewing",
        },

        "flow.back": {
          target: "idle",
          actions: "resetContext",
        },
      },
    },

    setupTeams: {
      on: {
        "teams.set": {
          actions: assign({
            teams: ({ event }) => (event.type === "teams.set" ? event.teams : []),
          }),
        },

        "teams.confirm": {
          target: "recording",
          guard: "hasMinimumTeams",
        },

        "flow.back": {
          target: "idle",
          actions: "resetContext",
        },

        "flow.reset": {
          target: "idle",
          actions: "resetContext",
        },
      },
    },

    recording: {
      on: {
        "rank.assign": [
          {
            guard: ({ context, event }) =>
              event.type === "rank.assign" &&
              context.rankEntries.some(([ti]) => ti === event.teamIndex),

            actions: assign({
              rankEntries: ({ context, event }) => {
                if (event.type !== "rank.assign") return context.rankEntries;

                const idx = context.rankEntries.findIndex(([ti]) => ti === event.teamIndex);
                return idx >= 0 ? context.rankEntries.slice(0, idx) : context.rankEntries;
              },
            }),
          },
          {
            actions: assign({
              rankEntries: ({ context, event }) => {
                if (event.type !== "rank.assign") return context.rankEntries;

                const rank = event.tie
                  ? Math.max(0, ...context.rankEntries.map(([, r]) => r)) || 1
                  : deriveNextRank(context.rankEntries);

                return [...context.rankEntries, [event.teamIndex, rank] as const];
              },
            }),
          },
        ],

        "rank.undo": {
          actions: assign({
            rankEntries: ({ context, event }) => {
              if (event.type !== "rank.undo") return context.rankEntries;

              const idx = context.rankEntries.findIndex(([ti]) => ti === event.teamIndex);
              return idx >= 0 ? context.rankEntries.slice(0, idx) : context.rankEntries;
            },
          }),
        },

        "results.submit": {
          target: "reviewing",
          guard: "allTeamsRanked",
        },

        "flow.back": [
          {
            target: "setupTeams",
            guard: "isTeamsActivity",
            actions: assign({ rankEntries: () => [] }),
          },
          {
            target: "idle",
            actions: "resetContext",
          },
        ],
      },
    },

    reviewing: {
      on: {
        "results.confirm": {
          target: "idle",
          actions: "resetContext",
        },

        "flow.back": [
          {
            target: "tournament",
            guard: "isTournamentActivity",
          },
          {
            target: "recording",
          },
        ],
      },
    },
  },

  on: {
    "flow.reset": {
      target: ".idle",
      actions: "resetContext",
    },
  },
});

export type MatchFlowMachine = typeof matchFlowMachine;
